import { JOINT_DEFS } from '../types';
import type { JointKey } from '../types';

// ===== 关节名规范化 =====

/**
 * 规范化关节名，消除不同 URDF/CSV 命名风格的差异。
 *
 * 支持的命名风格:
 *   - PascalCase:             Left_Hip_Pitch  → left_hip_pitch
 *   - snake_case_joint:       left_hip_pitch_joint → left_hip_pitch
 *   - A-前缀:                 ALeft_Shoulder_Pitch → left_shoulder_pitch
 *   - jXX_ 编号前缀 + _l/_r:  J00_HIP_PITCH_L → left_hip_pitch
 *   - UPPERCASE:              J12_WAIST_YAW → waist_yaw
 */
export function normalizeJointName(name: string): string {
  let normalized = name
    .toLowerCase()
    .replace(/^a/, '')           // 去掉前导 'A'（如 ALeft_Shoulder_Pitch）
    .replace(/^j\d+_/, '')       // 去掉 jXX_ 前缀（如 j12_waist_yaw）
    .replace(/_joint$/, '')      // 去掉 _joint 后缀
    .replace(/[^a-z0-9]+/g, '_') // 非字母数字 → 下划线
    .replace(/^_|_$/g, '')       // 去掉首尾下划线
    .replace(/_+/g, '_');        // 合并连续下划线

  // 处理 _l / _r 尾缀 → left_ / right_ 前缀
  // 例: hip_pitch_l → left_hip_pitch (PM01 风格)
  if (normalized.endsWith('_l')) {
    normalized = 'left_' + normalized.slice(0, -2);
  } else if (normalized.endsWith('_r')) {
    normalized = 'right_' + normalized.slice(0, -2);
  }

  return normalized;
}

// ===== JointKey 规范化缓存 =====

const ALL_JOINT_KEYS: JointKey[] = JOINT_DEFS.map((d) => d.key);

const NORMALIZED_JOINT_KEYS: Record<string, JointKey> = {};
for (const key of ALL_JOINT_KEYS) {
  NORMALIZED_JOINT_KEYS[normalizeJointName(key)] = key;
}

// ===== 兜底映射 =====
// 仅处理归一化后仍有歧义的简称（如 Waist 没有轴后缀，无法确定是 yaw/roll/pitch）。
// 能通过 normalize 精确匹配或唯一模糊匹配的关节不在此列。

const AMBIGUOUS_FALLBACK: Record<string, JointKey> = {
  Waist: 'waist_yaw_joint',
  // Unitree G1 只有单自由度肘关节；CSV/URDF 省略了 pitch 轴后缀。
  // 标准 JointKey 同时包含 elbow_pitch/elbow_yaw，必须显式消除歧义。
  left_elbow_joint: 'left_elbow_pitch_joint',
  right_elbow_joint: 'right_elbow_pitch_joint',
};

// ===== 匹配逻辑 =====

/**
 * 根据 CSV/URDF 字段名列表，动态构建 JointKey → URDF关节名 的映射。
 *
 * CSV 字段名即为 URDF 关节名，通过规范化和模糊匹配与标准 JointKey 关联。
 * 无法确定映射的字段会被静默跳过。
 *
 * @returns JointKey → URDF关节名（CSV字段名）的映射
 */
export function buildJointMapFromFieldNames(
  fieldNames: string[],
): Partial<Record<JointKey, string>> {
  const jointKeyToUrdf: Partial<Record<JointKey, string>> = {};
  const usedJointKeys = new Set<JointKey>();

  for (const field of fieldNames) {
    // 跳过 root_ 前缀的根位姿字段和空字段
    if (!field || field.startsWith('root_')) continue;

    // 1. 先查兜底映射
    const fallback = AMBIGUOUS_FALLBACK[field];
    if (fallback && !usedJointKeys.has(fallback)) {
      jointKeyToUrdf[fallback] = field;
      usedJointKeys.add(fallback);
      continue;
    }

    // 2. 规范化后精确匹配
    const normalized = normalizeJointName(field);
    if (!normalized) continue;

    const exactMatch = NORMALIZED_JOINT_KEYS[normalized];
    if (exactMatch && !usedJointKeys.has(exactMatch)) {
      jointKeyToUrdf[exactMatch] = field;
      usedJointKeys.add(exactMatch);
      continue;
    }

    // 3. 模糊匹配：规范化 csv 名与规范化 JointKey 互为子串
    //    仅在候选唯一时才采纳，避免歧义映射
    const candidates: JointKey[] = [];
    for (const nk of Object.keys(NORMALIZED_JOINT_KEYS)) {
      if (nk.includes(normalized) || normalized.includes(nk)) {
        const jk = NORMALIZED_JOINT_KEYS[nk];
        if (!usedJointKeys.has(jk)) {
          candidates.push(jk);
        }
      }
    }

    if (candidates.length === 1) {
      jointKeyToUrdf[candidates[0]] = field;
      usedJointKeys.add(candidates[0]);
    }
    // 多个候选或零候选 → 跳过
  }

  return jointKeyToUrdf;
}

/**
 * 构建双向映射，同时返回 CSV字段→JointKey 和 JointKey→CSV字段。
 */
export function buildBidirectionalMappings(fieldNames: string[]): {
  urdfToJointKey: Record<string, JointKey>;
  jointKeyToUrdf: Partial<Record<JointKey, string>>;
} {
  const jointKeyToUrdf = buildJointMapFromFieldNames(fieldNames);
  const urdfToJointKey: Record<string, JointKey> = {};
  for (const [jk, urdf] of Object.entries(jointKeyToUrdf)) {
    urdfToJointKey[urdf] = jk as JointKey;
  }
  return { urdfToJointKey, jointKeyToUrdf };
}
